import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, FileText, ImagePlus, Pencil, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Estado } from '../../componentes/estados';
import { Entrada, Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { BotaoIcone, Capa, enviarAnexo } from './comum';
import { PRECISA_BIBLIOTECA, PROVEDORES, duracaoTexto, limpar, type AulaDetalhe, type AulaItem, type CursoFicha, type ModuloItem, type Provedor } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

/** Invalida tudo do módulo que depende do curso (catálogo, ficha, aula, progresso). */
function useInvalidar() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['treinamento'] });
}

interface Props {
  /** Curso a editar; nulo abre o formulário de novo curso. */
  cursoId: string | null;
  aoFechar: () => void;
  /** Depois de criar, reabre o editor no curso novo para montar módulos e aulas. */
  aoCriar: (id: string) => void;
}

/** M11.1 e M11.6: editor do curso (dados, capa, módulos, aulas em ordem, pré-requisitos e material de apoio). */
export function EditorCurso({ cursoId, aoFechar, aoCriar }: Props) {
  const { t } = useTranslation('treinamento');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const invalidar = useInvalidar();
  const [remover, setRemover] = useState(false);
  const [excluido, setExcluido] = useState(false);
  const arquivo = useRef<HTMLInputElement>(null);
  const q = useQuery({ queryKey: ['treinamento', 'curso', cursoId], queryFn: () => get<CursoFicha>(`/treinamento/cursos/${cursoId}`), enabled: !!cursoId && !excluido });
  const curso = cursoId ? q.data : undefined;

  const excluir = useMutation({
    mutationFn: () => del(`/treinamento/cursos/${cursoId}`),
    onSuccess: async () => {
      setRemover(false);
      // O curso não existe mais: desliga a consulta e tira do cache antes de fechar, para não rebuscar um 404.
      setExcluido(true);
      qc.removeQueries({ queryKey: ['treinamento', 'curso', cursoId] });
      aoFechar();
      toast.sucesso(tn('comum.removido'));
      await invalidar();
    },
    onError: (e) => {
      setRemover(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  const capa = useMutation({
    mutationFn: async (f: File | null) => {
      const anexo = f ? await enviarAnexo('treinamento.curso', cursoId!, f) : null;
      await patch(`/treinamento/cursos/${cursoId}`, { capa_anexo_id: anexo });
    },
    onSuccess: async () => {
      await invalidar();
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const campos: DefCampo[] = [
    { nome: 'titulo', rotulo: t('campos.titulo'), tipo: 'texto', obrigatorio: true },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area' },
    { nome: 'carga_horaria_min', rotulo: t('campos.cargaHoraria'), tipo: 'numero', min: 0, passo: 1, largura: 'meia', ajuda: t('campos.cargaAjuda') },
    { nome: 'pre_requisito_curso_id', rotulo: t('campos.preRequisitoCurso'), tipo: 'busca', largura: 'meia', ajuda: t('campos.preRequisitoCursoAjuda'), busca: { caminho: '/treinamento/cursos', rotulo: (i) => String(i.titulo) } },
    ...(cursoId ? ([{ nome: 'publicado', rotulo: t('campos.publicado'), tipo: 'booleano', ajuda: t('campos.publicadoAjuda') }] as DefCampo[]) : []),
  ];

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={curso ? curso.titulo : t('acoes.novoCurso')}
      rodape={
        <>
          {curso && s.pode('treinamento.excluir') && (
            <Botao variante="perigo" className="mr-auto" onClick={() => setRemover(true)}>
              {tn('comum.excluir')}
            </Botao>
          )}
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-curso">
              {tn('comum.salvar')}
            </Botao>
          </>
        </>
      }
    >
      {cursoId && q.isPending ? (
        <Estado consulta={q}>{() => null}</Estado>
      ) : cursoId && q.isError ? (
        <Estado consulta={q}>{() => null}</Estado>
      ) : (
        <div className="flex min-w-0 flex-col gap-6">
          <Formulario
            key={curso?.id ?? 'novo'}
            id="form-curso"
            campos={campos}
            inicial={(curso ?? {}) as unknown as Record<string, unknown>}
            rotulosAtuais={curso ? { pre_requisito_curso_id: curso.pre_requisito_titulo ?? '' } : undefined}
            aoEnviar={async (v) => {
              const corpo = limpar({ titulo: v.titulo, descricao: v.descricao, carga_horaria_min: v.carga_horaria_min, pre_requisito_curso_id: v.pre_requisito_curso_id });
              if (curso) {
                await patch(`/treinamento/cursos/${curso.id}`, { ...corpo, publicado: !!v.publicado });
                await invalidar();
                toast.sucesso(tn('comum.salvo'));
              } else {
                const r = await post<{ id: string }>('/treinamento/cursos', corpo);
                await invalidar();
                toast.sucesso(t('mensagens.cursoCriado'));
                aoCriar(r.id);
              }
            }}
          />

          {curso && (
            <>
              <section aria-label={t('editor.capa')} className="flex flex-wrap items-center gap-4 border-t border-borda pt-4">
                <div className="aspect-video w-40 overflow-hidden rounded-lg border border-borda">
                  <Capa anexoId={curso.capa_anexo_id} titulo={curso.titulo} />
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="kicker">{t('editor.capa')}</h3>
                  <input ref={arquivo} type="file" accept="image/*" className="sr-only" tabIndex={-1} aria-label={t('editor.enviarCapa')} title={t('editor.enviarCapa')} onChange={(e) => e.target.files?.[0] && capa.mutate(e.target.files[0])} />
                  <div className="flex flex-wrap gap-2">
                    <Botao tamanho="sm" icone={<ImagePlus className="size-4" />} carregando={capa.isPending} onClick={() => arquivo.current?.click()}>
                      {t('editor.enviarCapa')}
                    </Botao>
                    {curso.capa_anexo_id && (
                      <Botao tamanho="sm" variante="fantasma" onClick={() => capa.mutate(null)}>
                        {t('editor.removerCapa')}
                      </Botao>
                    )}
                  </div>
                </div>
              </section>

              <Estrutura curso={curso} />

              <section aria-label={t('editor.atividade')} className="border-t border-borda pt-4">
                <h3 className="kicker mb-3">{t('editor.atividade')}</h3>
                <Chatter entidade="treinamento.curso" registroId={curso.id} titulo={curso.titulo} rotulosCampos={{ publicado: t('campos.publicado'), titulo: t('campos.titulo'), descricao: t('campos.descricao'), carga_horaria_min: t('campos.cargaHoraria') }} />
              </section>
            </>
          )}
        </div>
      )}
      <Confirmar aberto={remover} aoFechar={() => setRemover(false)} aoConfirmar={() => excluir.mutate()} titulo={t('confirmar.excluirCurso')} texto={t('confirmar.excluirCursoTexto')} perigoso carregando={excluir.isPending} />
    </Drawer>
  );
}

// ---- Estrutura: módulos e aulas ----

type AulaAberta = { aula: AulaItem } | { moduloId: string } | null;

function Estrutura({ curso }: { curso: CursoFicha }) {
  const { t } = useTranslation('treinamento');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const invalidar = useInvalidar();
  const [novoModulo, setNovoModulo] = useState('');
  // Val5 (29/10): módulo e aula novos pedem `treinamento.criar`; editar, renomear e excluir seguem com `treinamento.editar`.
  const podeCriar = useSessao().pode('treinamento.criar');
  const [renomear, setRenomear] = useState<ModuloItem | null>(null);
  const [aula, setAula] = useState<AulaAberta>(null);
  const [excluirModulo, setExcluirModulo] = useState<ModuloItem | null>(null);
  const [excluirAula, setExcluirAula] = useState<AulaItem | null>(null);
  const erro = (e: unknown) => toast.erro(mensagemDeErro(e));

  const criarModulo = useMutation({
    mutationFn: () => post('/treinamento/modulos', { curso_id: curso.id, titulo: novoModulo }),
    onSuccess: async () => {
      setNovoModulo('');
      await invalidar();
    },
    onError: erro,
  });
  const reordenar = useMutation({
    mutationFn: (grade: { id: string; aulas: string[] }[]) => post(`/treinamento/cursos/${curso.id}/reordenar`, { modulos: grade }),
    onSuccess: invalidar,
    onError: erro,
  });
  const removerModulo = useMutation({
    mutationFn: (id: string) => del(`/treinamento/modulos/${id}`),
    onSuccess: async () => {
      setExcluirModulo(null);
      await invalidar();
    },
    onError: (e) => {
      setExcluirModulo(null);
      erro(e);
    },
  });
  const removerAula = useMutation({
    mutationFn: (id: string) => del(`/treinamento/aulas/${id}`),
    onSuccess: async () => {
      setExcluirAula(null);
      await invalidar();
    },
    onError: (e) => {
      setExcluirAula(null);
      erro(e);
    },
  });

  const grade = () => curso.modulos_itens.map((m) => ({ id: m.id, aulas: m.aulas.map((a) => a.id) }));
  const moverModulo = (i: number, d: -1 | 1) => {
    const g = grade();
    [g[i], g[i + d]] = [g[i + d]!, g[i]!];
    reordenar.mutate(g);
  };
  const moverAula = (mi: number, ai: number, d: -1 | 1) => {
    const g = grade();
    const l = g[mi]!.aulas;
    [l[ai], l[ai + d]] = [l[ai + d]!, l[ai]!];
    reordenar.mutate(g);
  };

  return (
    <section aria-label={t('editor.estrutura')} className="border-t border-borda pt-4">
      <h3 className="kicker mb-1">{t('editor.estrutura')}</h3>
      <p className="mb-3 text-xs text-tinta-3">{t('editor.estruturaAjuda')}</p>

      {curso.modulos_itens.length === 0 && <p className="mb-3 text-sm text-tinta-3">{t('vazio.semModulos')}</p>}
      <ol className="flex flex-col gap-4">
        {curso.modulos_itens.map((m, mi) => (
          <li key={m.id} className="min-w-0 rounded-lg border border-borda">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 border-b border-borda bg-sup-2 px-3 py-2">
              <TextoCortado como="h4" className="min-w-0 text-sm font-semibold">
                <span className="numero mr-2 text-tinta-3">{mi + 1}</span>
                {m.titulo}
              </TextoCortado>
              <div className="flex items-center gap-1">
                <BotaoIcone rotulo={t('acoes.subir')} disabled={mi === 0 || reordenar.isPending} onClick={() => moverModulo(mi, -1)} icone={<ArrowUp className="size-4" />} />
                <BotaoIcone rotulo={t('acoes.descer')} disabled={mi === curso.modulos_itens.length - 1 || reordenar.isPending} onClick={() => moverModulo(mi, 1)} icone={<ArrowDown className="size-4" />} />
                <BotaoIcone rotulo={t('acoes.renomear')} onClick={() => setRenomear(m)} icone={<Pencil className="size-4" />} />
                <BotaoIcone rotulo={tn('comum.excluir')} onClick={() => setExcluirModulo(m)} icone={<Trash2 className="size-4" />} perigo />
              </div>
            </div>
            {m.aulas.length === 0 ? (
              <p className="px-3 py-3 text-sm text-tinta-3">{t('vazio.semAulasModulo')}</p>
            ) : (
              <ol className="divide-y divide-borda">
                {m.aulas.map((a, ai) => (
                  <li key={a.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <TextoCortado como="p" className="">
                        <span className="numero mr-2 text-xs text-tinta-3">{ai + 1}</span>
                        {a.titulo}
                      </TextoCortado>
                      <p className="flex flex-wrap items-center gap-x-2 text-xs text-tinta-3">
                        <span>{a.tipo === 'texto' ? t('area.aulaTexto') : t(`provedores.${a.provedor}`)}</span>
                        <span className="numero">{duracaoTexto(a.duracao_min)}</span>
                        {a.pre_requisito_aula_id && <Selo>{t('editor.temPreRequisito')}</Selo>}
                        {a.materiais > 0 && (
                          <span className="inline-flex items-center gap-1">
                            <FileText className="size-3" aria-hidden />
                            <span className="numero">{a.materiais}</span>
                          </span>
                        )}
                        {!a.provedor_disponivel && <Selo cor="laranja">{t('editor.provedorDesabilitado')}</Selo>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <BotaoIcone rotulo={t('acoes.subir')} disabled={ai === 0 || reordenar.isPending} onClick={() => moverAula(mi, ai, -1)} icone={<ArrowUp className="size-4" />} />
                      <BotaoIcone rotulo={t('acoes.descer')} disabled={ai === m.aulas.length - 1 || reordenar.isPending} onClick={() => moverAula(mi, ai, 1)} icone={<ArrowDown className="size-4" />} />
                      <BotaoIcone rotulo={tn('comum.editar')} onClick={() => setAula({ aula: a })} icone={<Pencil className="size-4" />} />
                      <BotaoIcone rotulo={tn('comum.excluir')} onClick={() => setExcluirAula(a)} icone={<Trash2 className="size-4" />} perigo />
                    </div>
                  </li>
                ))}
              </ol>
            )}
            {podeCriar && (
              <div className="border-t border-borda px-3 py-2">
                <Botao tamanho="sm" variante="fantasma" icone={<Plus className="size-4" />} onClick={() => setAula({ moduloId: m.id })}>
                  {t('acoes.novaAula')}
                </Botao>
              </div>
            )}
          </li>
        ))}
      </ol>

      {podeCriar && (
        <form
          className="mt-4 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (novoModulo.trim()) criarModulo.mutate();
          }}
        >
          <div className="min-w-0 flex-1 basis-48">
            <Entrada aria-label={t('campos.tituloModulo')} placeholder={t('campos.tituloModulo')} maxLength={160} value={novoModulo} onChange={(e) => setNovoModulo(e.target.value)} />
          </div>
          <Botao type="submit" icone={<Plus className="size-4" />} carregando={criarModulo.isPending} disabled={!novoModulo.trim()}>
            {t('acoes.novoModulo')}
          </Botao>
        </form>
      )}

      {renomear && <RenomearModulo modulo={renomear} aoFechar={() => setRenomear(null)} />}
      {aula && <FormAula curso={curso} aberta={aula} aoFechar={() => setAula(null)} />}
      <Confirmar
        aberto={!!excluirModulo}
        aoFechar={() => setExcluirModulo(null)}
        aoConfirmar={() => excluirModulo && removerModulo.mutate(excluirModulo.id)}
        titulo={t('confirmar.excluirModulo')}
        texto={t('confirmar.excluirModuloTexto')}
        perigoso
        carregando={removerModulo.isPending}
      />
      <Confirmar
        aberto={!!excluirAula}
        aoFechar={() => setExcluirAula(null)}
        aoConfirmar={() => excluirAula && removerAula.mutate(excluirAula.id)}
        titulo={t('confirmar.excluirAula')}
        texto={t('confirmar.excluirAulaTexto')}
        perigoso
        carregando={removerAula.isPending}
      />
    </section>
  );
}

function RenomearModulo({ modulo, aoFechar }: { modulo: ModuloItem; aoFechar: () => void }) {
  const { t } = useTranslation('treinamento');
  const { t: tn } = useTranslation();
  const invalidar = useInvalidar();
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('acoes.renomear')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-modulo">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        id="form-modulo"
        campos={[{ nome: 'titulo', rotulo: t('campos.tituloModulo'), tipo: 'texto', obrigatorio: true }]}
        inicial={{ titulo: modulo.titulo }}
        aoEnviar={async (v) => {
          await patch(`/treinamento/modulos/${modulo.id}`, { titulo: v.titulo });
          await invalidar();
          aoFechar();
        }}
      />
    </Modal>
  );
}

// ---- Aula: formulário e material de apoio ----

function FormAula({ curso, aberta, aoFechar }: { curso: CursoFicha; aberta: NonNullable<AulaAberta>; aoFechar: () => void }) {
  const { t } = useTranslation('treinamento');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const invalidar = useInvalidar();
  const edicao = 'aula' in aberta ? aberta.aula : null;
  const modulos = curso.modulos_itens;
  const outras = modulos.flatMap((m) => m.aulas).filter((a) => a.id !== edicao?.id);
  // Só os provedores habilitados em Integrações; o da própria aula continua listado para não perdê-lo na edição.
  const provedores: Provedor[] = PROVEDORES.filter((p) => curso.players.habilitados.includes(p) || p === edicao?.provedor);
  const padrao: Provedor = provedores[0] ?? 'youtube';
  // Fase 2 (W17, K1): a aula de texto (curso de novidades) edita título, módulo, duração, pré-requisito e o texto; sem vídeo.
  const texto = edicao?.tipo === 'texto';
  const deVideo = new Set(['provedor', 'video_id', 'biblioteca']);

  const todos: DefCampo[] = [
    { nome: 'titulo', rotulo: t('campos.titulo'), tipo: 'texto', obrigatorio: true },
    { nome: 'modulo_id', rotulo: t('campos.modulo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: modulos.map((m) => ({ valor: m.id, rotulo: m.titulo })) },
    { nome: 'duracao_min', rotulo: t('campos.duracao'), tipo: 'numero', min: 0, passo: 1, largura: 'meia' },
    { nome: 'provedor', rotulo: t('campos.provedor'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: PROVEDORES.map((p) => ({ valor: p, rotulo: t(`provedores.${p}`) })).filter((o) => provedores.includes(o.valor as Provedor)) },
    { nome: 'video_id', rotulo: t('campos.videoId'), tipo: 'texto', obrigatorio: true, largura: 'meia', ajuda: t('campos.videoIdAjuda') },
    { nome: 'biblioteca', rotulo: t('campos.biblioteca'), tipo: 'texto', obrigatorio: true, ajuda: t('campos.bibliotecaAjuda'), visivel: (v) => PRECISA_BIBLIOTECA[(v.provedor as Provedor) ?? padrao] },
    { nome: 'pre_requisito_aula_id', rotulo: t('campos.preRequisitoAula'), tipo: 'selecao', ajuda: t('campos.preRequisitoAulaAjuda'), opcoes: outras.map((a) => ({ valor: a.id, rotulo: a.titulo })) },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area', obrigatorio: texto },
  ];
  const campos = texto ? todos.filter((c) => !deVideo.has(c.nome)) : todos;

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={edicao ? edicao.titulo : t('acoes.novaAula')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-aula">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      {!curso.players.conectado && !texto && (
        <p role="status" className="mb-4 flex items-start gap-2 rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-2">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-laranja" aria-hidden />
          <span>
            {t('area.playersDesconectados')}{' '}
            <Link to="/integracoes/integracoes" className="underline">
              {tn('comum.irParaIntegracoes')}
            </Link>
          </span>
        </p>
      )}
      <Formulario
        key={edicao?.id ?? 'nova'}
        id="form-aula"
        campos={campos}
        inicial={edicao ? ({ ...edicao, modulo_id: modulos.find((m) => m.aulas.some((a) => a.id === edicao.id))?.id } as unknown as Record<string, unknown>) : { modulo_id: 'moduloId' in aberta ? aberta.moduloId : modulos[0]?.id, provedor: padrao }}
        aoEnviar={async (v) => {
          const video = texto
            ? {}
            : {
                provedor: v.provedor,
                video_id: typeof v.video_id === 'string' ? v.video_id.trim() : v.video_id,
                biblioteca: PRECISA_BIBLIOTECA[v.provedor as Provedor] ? v.biblioteca : null,
              };
          const corpo = limpar({
            modulo_id: v.modulo_id,
            titulo: v.titulo,
            ...video,
            duracao_min: v.duracao_min,
            descricao: v.descricao,
            pre_requisito_aula_id: v.pre_requisito_aula_id,
          });
          if (edicao) await patch(`/treinamento/aulas/${edicao.id}`, corpo);
          else await post('/treinamento/aulas', corpo);
          await invalidar();
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      />
      {edicao && <MaterialDeApoio aulaId={edicao.id} />}
    </Modal>
  );
}

/** M11.6: material de apoio da aula, enviado como anexo do núcleo (entidade `treinamento.aula`). */
function MaterialDeApoio({ aulaId }: { aulaId: string }) {
  const { t } = useTranslation('treinamento');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const invalidar = useInvalidar();
  const arquivo = useRef<HTMLInputElement>(null);
  const q = useQuery({ queryKey: ['treinamento', 'aula', aulaId], queryFn: () => get<AulaDetalhe>(`/treinamento/aulas/${aulaId}`) });
  const enviar = useMutation({
    mutationFn: (f: File) => enviarAnexo('treinamento.aula', aulaId, f),
    onSuccess: async () => {
      await invalidar();
      toast.sucesso(t('mensagens.materialEnviado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: (anexo: string) => del(`/treinamento/aulas/${aulaId}/materiais/${anexo}`),
    onSuccess: invalidar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  return (
    <section aria-label={t('area.materialApoio')} className="mt-6 border-t border-borda pt-4">
      <h3 className="kicker mb-1">{t('area.materialApoio')}</h3>
      <p className="mb-3 text-xs text-tinta-3">{t('editor.materialAjuda')}</p>
      <Estado consulta={q}>
        {(d) =>
          d.materiais.length === 0 ? (
            <p className="text-sm text-tinta-3">{t('area.semMaterial')}</p>
          ) : (
            <ul className="flex flex-col divide-y divide-borda">
              {d.materiais.map((m) => (
                <li key={m.id} className="flex min-w-0 items-center justify-between gap-3 py-2 text-sm">
                  <a href={`/api/v1/anexos/${m.id}`} download className="inline-flex min-w-0 items-center gap-2 text-acento-forte hover:underline">
                    <FileText className="size-4 shrink-0" aria-hidden />
                    <span className="truncate" title={m.nome}>{m.nome}</span>
                  </a>
                  <BotaoIcone rotulo={tn('comum.excluir')} onClick={() => remover.mutate(m.id)} icone={<Trash2 className="size-4" />} perigo />
                </li>
              ))}
            </ul>
          )
        }
      </Estado>
      <input ref={arquivo} type="file" className="sr-only" tabIndex={-1} aria-label={t('acoes.anexar')} title={t('acoes.anexar')} onChange={(e) => e.target.files?.[0] && enviar.mutate(e.target.files[0])} />
      <Botao className="mt-3" tamanho="sm" icone={<Plus className="size-4" />} carregando={enviar.isPending} onClick={() => arquivo.current?.click()}>
        {t('acoes.anexar')}
      </Botao>
    </section>
  );
}
